import OpenMeteo from "../../third_party/OpenMeteo.js";
import OpenElevation from "../../third_party/OpenElevation.js";
import OpenMeteoFlood from "../../third_party/OpenMeteoFlood.js";
import OpenMeteoAirQuality from "../../third_party/OpenMeteoAirQuality.js";
import Earthquake from "../../third_party/Earthquake.js";
import NaturalDisaster from "../../third_party/NaturalDisaster.js";
import DSD from "../../ents/regions/admin_regions/DSD.js";
import District from "../../ents/regions/admin_regions/District.js";
import Province from "../../ents/regions/admin_regions/Province.js";

class Place {
  static getEntTypeName() {
    return "Point Location";
  }
  constructor({ latLng, openMeteoData }) {
    this.latLng = latLng;
    this.openMeteoData = openMeteoData;
  }

  get id() {
    return `place-${this.latLng.lat}-${this.latLng.lng}`;
  }

  get supertitle() {
    return this.constructor.getEntTypeName();
  }

  get title() {
    return this.latLng.title;
  }

  get subtitle() {
    return "";
  }

  get url() {
    return `/Place/${this.latLng.id}`;
  }

  async loadDetails() {
    const [
      openMeteoData,
      openMeteoFloodData,
      openElevationData,
      earthquakeData,
      airQualityData,
      dsd,
    ] = await Promise.all([
      OpenMeteo.getData({ latLng: this.latLng }),
      OpenMeteoFlood.getData(this.latLng),
      OpenElevation.getData(this.latLng),
      Earthquake.loadAllRecent(),
      OpenMeteoAirQuality.getData({ latLng: this.latLng }),
      DSD.loadNearest(this.latLng),
    ]);

    this.openMeteoData = openMeteoData;
    this.openMeteoFloodData = openMeteoFloodData;
    this.openElevationData = openElevationData;
    this.earthquakeData = earthquakeData;
    this.airQualityData = airQualityData;

    this.dsd = dsd;
    this.dsd = await this.dsd.loadLandslideWarningData();

    const [district, province] = await Promise.all([
      District.loadFromId(this.dsd.districtId),
      Province.loadFromId(this.dsd.provinceId),
    ]);

    this.district = district;
    this.province = province;

    this.autoAlertList = [
      NaturalDisaster.getLandslideRiskScore({
        openMeteoData: this.openMeteoData,
        openElevationData: this.openElevationData,
      }),
      NaturalDisaster.getFloodRiskScore({
        openMeteoData: this.openMeteoData,
        openElevationData: this.openElevationData,
      }),
      NaturalDisaster.getTsunamiRiskScore({
        earthquakeData: this.earthquakeData,
      }),
      OpenMeteo.getDroughtRiskScore({ openMeteoData: this.openMeteoData }),
      OpenMeteo.getHeatRiskScore({ openMeteoData: this.openMeteoData }),
      OpenMeteoAirQuality.getAirQualityScore({
        airQualityData: this.airQualityData,
      }),
    ];

    return this;
  }

  static async loadData({ latLng }) {
    return { latLng };
  }

  static async load({ latLng }) {
    const data = await this.loadData({ latLng });
    return new Place(data);
  }

  static fromLatLng(latLng) {
    return new Place({ latLng, openMeteoData: undefined });
  }

  get landslideWarningLevel() {
    return (this.dsd && this.dsd.latestLandslideWarningLevel) || 0;
  }

  get officialAlertLevel() {
    return this.landslideWarningLevel;
  }

  get autoAlertLevel() {
    const autoAlertList = this.autoAlertList || [];
    return Math.max(...autoAlertList.map((alert) => alert.level));
  }

  get nOfficialAlerts() {
    return this.landslideWarningLevel > 0 ? 1 : 0;
  }

  get nAutoAlerts() {
    const autoAlertList = this.autoAlertList || [];
    return autoAlertList.filter((alert) => alert.level > 0).length;
  }

  get nAlerts() {
    return this.nOfficialAlerts + this.nAutoAlerts;
  }

  get alertLevel() {
    return Math.max(this.officialAlertLevel, this.autoAlertLevel);
  }

  static dedupeByLatLng(places) {
    return Object.values(
      Object.fromEntries(
        places
          .filter((place) => place && place.latLng)
          .map((place) => [place.latLng.id, place]),
      ),
    );
  }
}

export default Place;
